import {
  ActionButton,
  ArrowPopover,
  Caption,
  Icon,
  MetaValue,
  SubsectionTitle,
  TooltipIconButton,
  useMenu,
} from '@if/mui'
import { Box, Chip, Divider, Stack } from '@mui/material'
import { CalendarSimulationAction } from '@src/types/loan/loan-simulation'
import { ChevronUp, RotateCcw, Undo2, X } from 'lucide-react'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { CALENDAR_ACTION_ICON, describeCalendarAction } from './LoanCalendarChangeLog-selector'

interface LoanCalendarChangeLogProps {
  actions: CalendarSimulationAction[]
  /** Replays the actions before `index`, dropping that one and everything after it. */
  onUndoTo: (index: number) => void
  loading?: boolean
  disabled?: boolean
}

/**
 * The pending actions, oldest first, each with the way back to before it. A calendar
 * simulation *is* a stack of actions the server replays, so undoing one necessarily undoes
 * everything stacked on top — which is why every row offers "undo to here" rather than
 * pretending a middle action can be pulled out on its own.
 */
const LoanCalendarChangeLog = ({
  actions,
  onUndoTo,
  loading = false,
  disabled = false,
}: LoanCalendarChangeLogProps) => {
  const { t } = useTranslation()
  const [buttonProps, popoverProps, closePopover] = useMenu()

  if (actions.length === 0) return null

  const lastIndex = actions.length - 1

  return (
    <>
      <ActionButton {...buttonProps} startIcon={RotateCcw} endIcon={ChevronUp} loading={loading} disabled={disabled}>
        <Stack direction="row" spacing={1} alignItems="center" component="span">
          <span>{t('loan.calendar.changeLog.review')}</span>
          <Chip size="small" color="primary" label={actions.length} />
        </Stack>
      </ActionButton>

      <ArrowPopover {...popoverProps} side="top">
        <Stack sx={{ minWidth: 360, maxWidth: 460 }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2, pt: 2, pb: 1 }}
          >
            <SubsectionTitle>{t('loan.calendar.changeLog.title')}</SubsectionTitle>
            <TooltipIconButton title={t('common.close')} size="small" onClick={closePopover}>
              <Icon icon={X} size="sm" />
            </TooltipIconButton>
          </Stack>

          {actions.map((action, index) => {
            const { label, detail } = describeCalendarAction(action, t)
            return (
              <Box key={index}>
                <Divider />
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ px: 2, py: 1.5 }}>
                  <Icon icon={CALENDAR_ACTION_ICON[action.actionType]} size="sm" color="action" />
                  <MetaValue sx={{ flexGrow: 1 }}>{label}</MetaValue>
                  {detail && <Caption>{detail}</Caption>}
                  <ActionButton
                    size="small"
                    startIcon={Undo2}
                    onClick={() => {
                      onUndoTo(index)
                      closePopover()
                    }}
                    disabled={disabled || loading}
                  >
                    {index === lastIndex ? t('loan.calendar.changeLog.undo') : t('loan.calendar.changeLog.undoToHere')}
                  </ActionButton>
                </Stack>
              </Box>
            )
          })}

          <Divider />
          <Caption sx={{ px: 2, py: 1.5 }}>{t('loan.calendar.changeLog.hint')}</Caption>
        </Stack>
      </ArrowPopover>
    </>
  )
}

export default memo(LoanCalendarChangeLog)
